import React from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useThemeColors } from '@/hooks/useThemeColors';

interface ClassAccessDeniedMessageProps {
    accessDenialReason: 'no_enrollments' | 'not_enrolled_in_course' | 'account_paused' | 'enrollment_not_started';
    enrollmentStartsAt?: string | null;
}

const ClassAccessDeniedMessage: React.FC<ClassAccessDeniedMessageProps> = ({
    accessDenialReason,
    enrollmentStartsAt,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const getContent = () => {
        if (accessDenialReason === 'no_enrollments') {
            return {
                heading: t('course.classAccessDenied.noEnrollments.heading'),
                message: t('course.classAccessDenied.noEnrollments.message'),
            };
        }

        if (accessDenialReason === 'account_paused') {
            return {
                heading: t('course.classAccessDenied.accountPaused.heading'),
                message: t('course.classAccessDenied.accountPaused.message'),
            };
        }

        if (accessDenialReason === 'enrollment_not_started') {
            const dateLabel = enrollmentStartsAt
                ? new Date(enrollmentStartsAt).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                  })
                : null;
            return {
                heading: t('course.classAccessDenied.enrollmentNotStarted.heading'),
                message: dateLabel
                    ? t('course.classAccessDenied.enrollmentNotStarted.messageWithDate', { date: dateLabel })
                    : t('course.classAccessDenied.enrollmentNotStarted.message'),
            };
        }

        return {
            heading: t('course.classAccessDenied.notEnrolledInCourse.heading'),
            message: t('course.classAccessDenied.notEnrolledInCourse.message'),
        };
    };

    const content = getContent();

    return (
        <div className="relative aspect-video w-full bg-gray-900">
            <div className="flex h-full items-center justify-center">
                <div className="mx-auto max-w-md px-6 text-center">
                    <div className="mb-6 flex justify-center">
                        <div className={`rounded-full p-4 ${colors.accentBg}`}>
                            <Lock className={`h-12 w-12 ${colors.iconColor}`} />
                        </div>
                    </div>
                    <h2 className="mb-3 text-xl font-bold text-white">
                        {content.heading}
                    </h2>
                    <p className="text-gray-300">{content.message}</p>
                </div>
            </div>
        </div>
    );
};

export default ClassAccessDeniedMessage;
